import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as AppStoreIcon } from '../images/app-store.svg';
import { ReactComponent as PlayStoreIcon } from '../images/google-play.svg';
import { ReactComponent as SwissMadeIcon } from '../images/swissmade.svg';
import { ReactComponent as SwissStartupIcon } from '../images/member.svg';
import { useTranslation } from 'react-i18next';
import Modal from '../utils/Modal';
// Import the CSS module
import styles from './Footer.module.css'; // Make sure the path is correct

// Import PNG versions as fallback
import appStoreLogo from '../images/app-store-logo.png';
import googlePlayLogo from '../images/google-play-logo.png';

const Footer = () => {
  // Initialize useTranslation with explicit 'translation' namespace to ensure copyright text translates
  const { t, i18n } = useTranslation('translation');
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  // Listen for language changes to force re-render
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setCurrentLanguage(lng);
    };
    
    // Set initial language
    setCurrentLanguage(i18n.language);
    
    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const handleCreditsClick = () => {
    setShowCreditsModal(true);
  };

  const handleCloseModal = () => {
    setShowCreditsModal(false);
  };

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.singleRow}>
            {/* Copyright text with translation - key forces re-render on language change */}
            <p className={styles.copyright} key={`copyright-${currentLanguage}`}>
              {t('copyright')}
            </p>

            <div className={styles.imageGroup}>
              <a 
                href="https://apps.apple.com/app/searchcasa/id6739990000" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.appStoreButton}
                aria-label="Download on App Store"
              >
                <svg className={styles.appleLogo} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div className={styles.storeButtonText}>
                  <span className={styles.storeButtonTop}>Download on the</span>
                  <span className={styles.storeButtonBottom}>App Store</span>
                </div>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.ginitech.searchCasa&pcampaignid=web_share" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.googlePlayButton}
                aria-label="Get it on Google Play"
              >
                <svg className={styles.playLogo} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0L8 12L0 12Z" fill="#00A0FF"/>
                  <path d="M0 0L8 12L24 12Z" fill="#00C853"/>
                  <path d="M0 12L8 12L0 24Z" fill="#FFD600"/>
                  <path d="M8 12L24 12L0 24Z" fill="#FF6D00"/>
                </svg>
                <div className={styles.storeButtonText}>
                  <span className={styles.storeButtonTop}>GET IT ON</span>
                  <span className={styles.storeButtonBottom}>Google Play</span>
                </div>
              </a>
            </div>

            <div className={styles.imageGroup}>
              <SwissMadeIcon className={styles.icon1} alt="Swiss Made" />
              <SwissStartupIcon className={styles.icon4} alt="Swiss Startup" />
            </div>

            <div className={styles.textGroup}>
              {/* Using t() for translation keys 'QA' and 'support' */}
              <Link to="/qa" className={styles.footerItem}>{t('QA')}</Link>
              <Link to="/support" className={styles.footerItem}>{t('support')}</Link>
            </div>

            <div className={styles.textGroup}>
              {/* Using t() for translation keys 'terms' and 'privacy' */}
              <Link to="/terms" className={styles.footerItem}>
                {t('terms')}
              </Link>
              <Link to="/privacy" className={styles.footerItem}>
                {t('privacy')}
              </Link>
            </div>

            <div className={styles.textGroup}>
              {/* Using t() for translation key 'credits' - make it clickable */}
              <p 
                className={styles.footerItem} 
                style={{ cursor: 'pointer' }}
                onClick={handleCreditsClick}
              >
                {t('credits')}
              </p>
              {/* Version number with translation */}
              <p className={styles.footerItem}>{t('version')}</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Credits Modal */}
      {showCreditsModal && (
        <Modal onClose={handleCloseModal}>
          <div className={styles.creditsModalContent}>
            <h2 className={styles.creditsTitle}>{t('credits')}</h2>
            <div className={styles.creditsInfo}>
              <p className={styles.creditsText}>{t('credits_company_name')}</p>
              <p className={styles.creditsText}>{t('credits_address_line1')}</p>
              <p className={styles.creditsText}>{t('credits_address_line2')}</p>
              <p className={styles.creditsText}>{t('credits_country')}</p>
              
              <div className={styles.creditsLinks}>
                <a 
                  href={`mailto:${t('credits_email')}`} 
                  className={styles.creditsLink}
                >
                  {t('credits_email')}
                </a>
                <a 
                  href={t('credits_website')} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.creditsLink}
                >
                  {t('credits_website')}
                </a>
              </div>
              
              <p className={styles.creditsText}>{t('credits_phone')}</p>
              <p className={styles.creditsText}>{t('credits_registration')}</p>
            </div>
            <button 
              className={styles.creditsCloseButton}
              onClick={handleCloseModal}
            >
              {t('close')}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Footer;
